import { Container, Sprite, Text, type Texture } from 'pixi.js';

// The game window, layered bottom-up:
//   paper sheet -> tab content -> frame (transparent hole) -> tab labels.
// The frame is drawn OVER the content so its edges overlap e.g. the city map;
// it is not interactive, so taps pass through the hole to the content. The
// paper sheet is hidden on tabs that are not "drawn on paper" (city).
// Layout fractions are relative to the window sprite size.
const TABS_Y = 0.072;
const TAB_X_START = 0.44;
const TAB_X_SPACING = 0.065;
const TAB_FONT = 0.014;
const TAB_COLOR = 0xffffff;
const TAB_INACTIVE_ALPHA = 0.45;
// Paper content area (right side of the window). Fractions measured from the
// actual paper pixels in paper.png (x 745..2575, y 198..1440 of 2752x1536) -
// content fills the paper edge to edge.
const CONTENT_X = 0.268;
const CONTENT_Y = 0.127;
const CONTENT_W = 0.675;
const CONTENT_H = 0.812;
// Inner padding (fraction of window height, all four sides) for paper tabs
// (skills / fights) so cards and rows don't touch the paper edges. The city
// map intentionally has none - it runs edge to edge under the frame.
const PAPER_CONTENT_PADDING = 0.03;

export type GameTabId = 'city' | 'skills' | 'fights';

// Tabs whose content sits on the paper sheet. City shows the map instead.
const PAPER_TABS: ReadonlySet<GameTabId> = new Set(['skills', 'fights']);

const TABS: { id: GameTabId; label: string }[] = [
  { id: 'city', label: 'CITY' },
  { id: 'skills', label: 'SKILLS' },
  { id: 'fights', label: 'FIGHTS' },
];

export type GameWindowOptions = {
  // Frame art with a transparent content hole.
  frame: Texture;
  // Paper sheet art, same canvas size as the frame (1:1 overlay).
  paper: Texture;
  // Window height in design px; width follows the frame texture aspect.
  height: number;
  fontFamily?: string;
  textResolution: number;
  // Builds the content container for a tab, sized to the paper area. Return
  // null for a not-yet-implemented tab (paper stays empty).
  buildContent: (id: GameTabId, width: number, height: number) => Container | null;
  initialTab?: GameTabId;
};

export class GameWindow extends Container {
  // Frame art - DesktopScreen uses it to compute the 1:1 overlay transform.
  readonly texture: Texture;
  readonly windowWidth: number;
  readonly windowHeight: number;

  private readonly paperSprite: Sprite;
  private readonly tabLabels = new Map<GameTabId, Text>();
  private readonly tabContent = new Map<GameTabId, Container | null>();
  private activeTab: GameTabId;

  constructor(opts: GameWindowOptions) {
    super();
    const { frame, paper, height: h } = opts;
    const w = h * (frame.width / frame.height);
    this.texture = frame;
    this.windowWidth = w;
    this.windowHeight = h;

    this.paperSprite = new Sprite(paper);
    this.paperSprite.width = w;
    this.paperSprite.height = h;
    this.addChild(this.paperSprite);

    const contentW = CONTENT_W * w;
    const contentH = CONTENT_H * h;

    for (const tab of TABS) {
      const pad = PAPER_TABS.has(tab.id) ? PAPER_CONTENT_PADDING * h : 0;
      const content = opts.buildContent(tab.id, contentW - pad * 2, contentH - pad * 2);
      if (content) {
        content.position.set(CONTENT_X * w + pad, CONTENT_Y * h + pad);
        content.visible = false;
        this.addChild(content);
      }
      this.tabContent.set(tab.id, content);
    }

    const frameSprite = new Sprite(frame);
    frameSprite.width = w;
    frameSprite.height = h;
    this.addChild(frameSprite);

    const tabStyle = {
      fontFamily: opts.fontFamily,
      fontSize: TAB_FONT * h,
      fontWeight: 'bold' as const,
      fill: TAB_COLOR,
      letterSpacing: 1.5,
    };

    TABS.forEach((tab, i) => {
      const label = new Text({ text: tab.label, style: tabStyle, resolution: opts.textResolution });
      label.anchor.set(0.5);
      label.position.set((TAB_X_START + i * TAB_X_SPACING) * w, TABS_Y * h);
      label.eventMode = 'static';
      label.cursor = 'pointer';
      label.on('pointertap', () => this.setTab(tab.id));
      this.addChild(label);
      this.tabLabels.set(tab.id, label);
    });

    // STUDIO is a navigation link to the designer portal, not a content tab -
    // it does not participate in setTab (no content, no active state).
    const studioLabel = new Text({
      text: 'STUDIO',
      style: tabStyle,
      resolution: opts.textResolution,
    });
    studioLabel.anchor.set(0.5);
    studioLabel.position.set((TAB_X_START + TABS.length * TAB_X_SPACING) * w, TABS_Y * h);
    // Always shown as inactive - it's a link, never a selected tab.
    studioLabel.alpha = TAB_INACTIVE_ALPHA;
    studioLabel.eventMode = 'static';
    studioLabel.cursor = 'pointer';
    studioLabel.on('pointertap', () => {
      window.location.href = '/studio';
    });
    this.addChild(studioLabel);

    this.activeTab = opts.initialTab ?? 'fights';
    this.setTab(this.activeTab);
  }

  setTab(id: GameTabId): void {
    this.activeTab = id;
    this.paperSprite.visible = PAPER_TABS.has(id);
    for (const [tabId, label] of this.tabLabels) {
      label.alpha = tabId === id ? 1 : TAB_INACTIVE_ALPHA;
    }
    for (const [tabId, content] of this.tabContent) {
      if (content) content.visible = tabId === id;
    }
  }
}
